import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { AlertTriangle, User, Calendar, Clock, ChevronDown, ChevronUp, Bell, CheckCircle } from 'lucide-react';

const BarberViolations = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBarber, setExpandedBarber] = useState(null);
  const [barberDetails, setBarberDetails] = useState({});

  useEffect(() => {
    fetchStats();
    fetchAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/barber-violations/stats');
      setStats(res.data);
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل تحميل إحصائيات المخالفات' : 'Failed to load violation stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await api.get('/barber-violations/alerts');
      setAlerts(res.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const fetchBarberDetails = async (barberId) => {
    if (barberDetails[barberId]) {
      setExpandedBarber(expandedBarber === barberId ? null : barberId);
      return;
    }

    try {
      const res = await api.get(`/barber-violations/barber/${barberId}`);
      setBarberDetails(prev => ({ ...prev, [barberId]: res.data }));
      setExpandedBarber(barberId);
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل تحميل تفاصيل المخالفات' : 'Failed to load violation details');
    }
  };

  const markAlertRead = async (alertId) => {
    try {
      await api.put(`/barber-violations/alerts/${alertId}/read`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success(language === 'ar' ? 'تم وضع علامة مقروء' : 'Marked as read');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في وضع علامة مقروء' : 'Failed to mark as read');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return language === 'ar' 
      ? date.toLocaleDateString('ar-AE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getViolationTypeLabel = (type) => {
    const types = {
      'invalid_time': language === 'ar' ? 'وقت غير صالح' : 'Invalid Time',
      'invalid_date': language === 'ar' ? 'تاريخ غير صالح' : 'Invalid Date',
      'other': language === 'ar' ? 'أخرى' : 'Other'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* العنوان */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'مخالفات الحلاقين' : 'Barber Violations'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'تتبع محاولات الإدخال الخاطئة' : 'Track incorrect input attempts'}
            </p>
          </div>
        </div>

        {/* التنبيهات */}
        {alerts.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-red-400 font-semibold">
              <Bell className="w-5 h-5" />
              <span>{language === 'ar' ? 'تنبيهات جديدة' : 'New Alerts'}</span>
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{alerts.length}</span>
            </div>
            {alerts.map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-card/50 rounded-lg p-3">
                <div>
                  <p className="font-medium">{alert.message}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(alert.created_at)}</p>
                </div>
                <Button
                  onClick={() => markAlertRead(alert.id)}
                  variant="ghost"
                  size="sm"
                  className="text-green-500"
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* ملخص */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-white/5 rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'ar' ? 'إجمالي المخالفات هذا الشهر' : 'Total Violations This Month'}
            </div>
            <div className="text-3xl font-bold text-orange-500">
              {stats?.total_violations_this_month || 0}
            </div>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'ar' ? 'حلاقين تحت المراقبة' : 'Barbers Under Watch'}
            </div>
            <div className="text-3xl font-bold text-red-500">
              {stats?.barbers_with_alerts || 0}
            </div>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'ar' ? 'إجمالي الحلاقين' : 'Total Barbers'}
            </div>
            <div className="text-3xl font-bold text-primary">
              {stats?.stats?.length || 0}
            </div>
          </div>
        </div>

        {/* جدول الحلاقين */}
        <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h2 className="font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              {language === 'ar' ? 'مخالفات كل حلاق' : 'Violations by Barber'}
            </h2>
          </div>
          <div className="divide-y divide-white/5">
            {stats?.stats?.map(barber => (
              <div key={barber.barber_id}>
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => fetchBarberDetails(barber.barber_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="font-bold text-primary">
                        {barber.barber_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">{barber.barber_name}</div>
                      {barber.alert && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                          {language === 'ar' ? 'يحتاج مراقبة' : 'Needs attention'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'هذا الشهر' : 'This Month'}
                      </div>
                      <div className={`font-bold ${barber.violations_this_month >= 5 ? 'text-red-500' : barber.violations_this_month >= 3 ? 'text-orange-500' : 'text-foreground'}`}>
                        {barber.violations_this_month}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">
                        {language === 'ar' ? '6 شهور' : '6 Months'}
                      </div>
                      <div className="font-bold">{barber.violations_last_6_months}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'الإجمالي' : 'Total'}
                      </div>
                      <div className="font-bold">{barber.total_violations}</div>
                    </div>
                    {expandedBarber === barber.barber_id ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* تفاصيل المخالفات */}
                {expandedBarber === barber.barber_id && barberDetails[barber.barber_id] && (
                  <div className="bg-secondary/30 p-4 space-y-2">
                    {barberDetails[barber.barber_id].violations.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        {language === 'ar' ? 'لا توجد مخالفات في آخر 6 شهور' : 'No violations in the last 6 months'}
                      </p>
                    ) : (
                      barberDetails[barber.barber_id].violations.map((violation, idx) => (
                        <div key={idx} className="bg-card rounded-lg p-3 flex items-start gap-3">
                          <AlertTriangle className="w-4 h-4 text-orange-500 mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{getViolationTypeLabel(violation.violation_type)}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(violation.created_at)}</span>
                            </div>
                            {violation.details && (
                              <div className="text-sm text-muted-foreground space-y-1">
                                {violation.details.attempted_date && (
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-3 h-3" />
                                    <span>{language === 'ar' ? 'التاريخ المُدخل:' : 'Entered Date:'} {violation.details.attempted_date}</span>
                                  </div>
                                )}
                                {violation.details.attempted_time && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-3 h-3" />
                                    <span>{language === 'ar' ? 'الوقت المُدخل:' : 'Entered Time:'} {violation.details.attempted_time}</span>
                                  </div>
                                )}
                                {violation.details.current_time && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-3 h-3" />
                                    <span>{language === 'ar' ? 'الوقت الفعلي:' : 'Actual Time:'} {violation.details.current_time}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarberViolations;
